import { Avatar, Drawer, useMediaQuery } from "@mui/material";
import { Close } from "@mui/icons-material";
import Typography from "../Typography/Typography";
import Box from "component/layoutComponents/Box/Box";
import React, { ChangeEvent, ReactNode, useState } from "react";
import { useTheme } from "styled-components";
import {
  StyleStartRequestButton,
  StyledOrderRequest,
  StyledTitle,
  StyledCloseIcon,
  StyledTerm,
  StyledDescription,
  StyledApprovalForm,
  StyledButtonSubmit,
} from "./OrderRequest.style";
import Section from "./Section";
import DisplayAddress from "./DisplayAddress";
import DefaultUptimeTheme from "component/theme/defaultTheme";
import { Button } from "../ButtonComponents";
import TextField from "../TextField";
import Dialog from "../Dialog";
import { getAvatarName } from "utils/functions";
import { TOP_NAV_MOBILE_HEIGHT } from "component/layoutComponents/topNav/topNav.style";

const BUSINESS_JUSTIFICATION_MAX_LENGTH = 100;

type FormDataType = Record<string, any>;
export interface IOrderRequestProps {
  approvalFrom: string;
  addressItem?: any;
  addressConfig?: any;
  onSubmit: (data: FormDataType) => void;
  onSaveAddress?: (address: any) => void;
  locale?: Record<string, any>;
  isNewJoiner?: boolean;
  isSoftWare?: boolean;
  isRequireApproval?: string;
  submitting?: boolean;
}

const OrderRequest = (props: IOrderRequestProps) => {
  const {
    approvalFrom,
    addressItem,
    addressConfig,
    onSubmit,
    onSaveAddress,
    locale,
    isNewJoiner,
    isSoftWare,
    isRequireApproval = 'yes',
    submitting,
  } = props;

  const [openDrawer, setOpenDrawer] = useState(false);
  const [formData, setFormData] = useState<FormDataType>({
    approvalFrom,
    cost_center: "",
    business_justification: "",
  });
  const [isOpenTerm, setIsOpenTerm] = useState(false);
  const confirmTermDialog = (
    <>
      <Button
        onClick={() => {
          setIsOpenTerm(false);
        }}
        variant="text"
        size="medium"
      >
        {locale?.shoppingCart?.errorDialog?.btnConfirm}
      </Button>
    </>
  );

  const handleTextFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (
      name === "business_justification" &&
      value.length > BUSINESS_JUSTIFICATION_MAX_LENGTH
    )
      return;
    setFormData((formData) => ({ ...formData, [name]: value }));
  };

  const handleSubmit = () => {
    onSubmit(formData);
  };

  const handleOpenTerm = () => {
    setIsOpenTerm(true);
  };
  const TOSContent = () => {
    return (
      <Typography>
        {locale?.shoppingCart?.tosContent}
      </Typography>
    );
  };

  return (
    <OrderRequestWrapper
      openDrawer={openDrawer}
      onOpen={() => setOpenDrawer(true)}
      locale={locale}
    >
      <StyledOrderRequest>
        <Box order={1} marginBottom="-16px">
          <StyledTitle>
            <StyledCloseIcon onClick={() => setOpenDrawer(false)}>
              <Close />
            </StyledCloseIcon>

            <Typography variant="h4" fontWeight="600">
              {locale?.shoppingCart?.orderRequest}
            </Typography>
          </StyledTitle>

          <StyledDescription variant="body2">
            {locale?.shoppingCart?.orderRequestMsg}
          </StyledDescription>
          {isRequireApproval === 'yes' ? (
          <Section variant="h6" title={locale?.shoppingCart?.approvalFrom}>
            <StyledApprovalForm>
              <Avatar>{getAvatarName(approvalFrom)}</Avatar>

              <Typography variant="h6">{approvalFrom}</Typography>
            </StyledApprovalForm>
          </Section>
          ) : <></>}

          {!isSoftWare && (<DisplayAddress
            onSubmit={onSaveAddress}
            addressConfig={addressConfig}
            addressItem={addressItem}
            locale={locale}
            disabled={submitting}
          />
          )}
          {(!isNewJoiner && !isSoftWare) && (<Section variant="h6" title={locale?.shoppingCart?.optionalDetails} disablePaddingBottom collapsible>
            <TextField
              name="cost_center"
              fullWidth
              variant="outlined"
              label={locale?.shoppingCart?.costCenterCode}
              value={formData.cost_center}
              onChange={handleTextFieldChange}
              color="primary"
              disabled
            />
            <TextField
              name="business_justification"
              fullWidth
              variant="outlined"
              label={locale?.shoppingCart?.businessJustification}
              helperText={`${formData.business_justification.length}/${BUSINESS_JUSTIFICATION_MAX_LENGTH}`}
              value={formData.business_justification}
              onChange={handleTextFieldChange}
              color="primary"
              disabled
            />
          </Section>)}
        </Box>

        <StyledTerm variant="caption">
          {locale?.shoppingCart?.tosCaption}{' '}
          <Typography sx={{textDecoration: 'underline', cursor:'pointer'}} variant="caption" onClick={handleOpenTerm}>{locale?.shoppingCart?.tos}</Typography>.
        </StyledTerm>

        <StyledButtonSubmit className="submit">
          <Button size="large" fullWidth loadingStart={submitting} onClick={handleSubmit} id="btn-orderrequest-submit">
            {locale?.shoppingCart?.submitRequest}
          </Button>
        </StyledButtonSubmit>
      </StyledOrderRequest>
      <Dialog
        title={<Typography variant="h5">{locale?.shoppingCart?.tos}</Typography>}
        content={<TOSContent />}
        open={isOpenTerm}
        onClose={() => setIsOpenTerm(false)}
        actions={confirmTermDialog}
        maxWidth="xs"
      />
    </OrderRequestWrapper>
  );
};

export default OrderRequest;

interface OrderRequestWrapperProps {
  children: ReactNode;
  openDrawer: boolean;
  onOpen: () => void;
  locale: any;
}

const OrderRequestWrapper = (props: OrderRequestWrapperProps) => {
  const { children, openDrawer, onOpen, locale } = props;

  const theme = useTheme() as typeof DefaultUptimeTheme;

  const matches = useMediaQuery(`(min-width:${theme.breakpoints.md})`);

  if (matches) return <>{children}</>;

  return (
    <>
      <StyleStartRequestButton>
        <Button onClick={onOpen}>{locale?.shoppingCart?.startRequest}</Button>
      </StyleStartRequestButton>

      <Drawer
        anchor="bottom"
        open={openDrawer}
        PaperProps={{
          sx: {
            top: `${TOP_NAV_MOBILE_HEIGHT}px`,
          },
        }}
      >
        {children}
      </Drawer>
    </>
  );
};

export const defaultProps = {
  locale: {
    shoppingCart: {
      orderRequest: 'Order Request',
      orderRequestMsg: 'You must submit a request approval. Once approved, your order will be placed.',
      approvalFrom: 'Approval from',
      optionalDetails: 'Optional details',
      costCenterCode: 'Cost Center Code (optional)',
      businessJustification: 'Business justification (optional)',
      noAddressSet: 'No address set',
      editShippingAddress: 'Edit shipping address',
      cancel: 'Cancel',
      saveAddress: 'Save Address',
      shippingTo: 'Shipping to',
      tosContent: 'I acknowledge and agree that the items I am ordering/receiving are Company property to be used for Company work related activities, and as such, the Company reserves the right to request return of the property upon my exit from the Company (or sooner, if required by the Company).',
      tosCaption: 'By clicking the Submit Request button, you agree to the',
      tos: 'Terms of Service',
      submittedMsg: 'Your request has been submitted for approval.',
      submitRequestMsg: 'Please allow time for approval. You can view your order request status in My Orders.',
      startRequest: 'Start Request',
      submitRequest: 'Submit Request',
      errorDialog: {
        title: 'Error Message',
        content: 'Something went wrong, please check with your administration.',
        btnConfirm: 'OK',
      },
    },
  },
};

OrderRequest.defaultProps = {
  ...defaultProps,
};
